import { FC } from 'react';
import ReactHtmlParser, {
  convertNodeToElement,
  Transform,
} from 'react-html-parser';
import styled from 'styled-components';
import { themeConfig, useThemeContext } from '@/shared/theme';

type Props = {
  value: string;
};

export const Text: FC<Props> = ({ value }) => {
  const { theme } = useThemeContext();

  // Clean up the text before parsing
  const cleanText = (text: string): string => {
    return (
      text
        // Remove escaped backslashes
        .replace(/\\\\/g, '')
        // Remove escaped asterisks
        .replace(/\\\*/g, '*')
        // Clean up markdown-style formatting
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Clean up list formatting
        .replace(/^- /gm, '• ')
        // Remove any remaining escape characters
        .replace(/\\([^\\])/g, '$1')
        // Fix double quotes
        .replace(/\\"/g, '"')
        // Clean up any remaining markdown artifacts
        .replace(/\*\\\*/g, '*')
        // Clean up multiple newlines
        .replace(/\n\s*\n/g, '\n')
    );
  };

  const transform: Transform = (node, index) => {
    if (node.type === 'tag' && node instanceof Element) {
      // Add custom styling for specific tags
      switch (node.name) {
        case 'p':
          return (
            <p
              key={index}
              style={{
                margin: '0.5em 0',
                lineHeight: '1.6',
                color: themeConfig[theme].colors.text,
              }}
            >
              {node.children?.map((child, i) =>
                convertNodeToElement(child, i, transform),
              )}
            </p>
          );
        case 'strong':
          return (
            <strong
              key={index}
              style={{
                fontWeight: 'bold',
                color: themeConfig[theme].colors.text,
              }}
            >
              {node.children?.map((child, i) =>
                convertNodeToElement(child, i, transform),
              )}
            </strong>
          );
        case 'ul':
        case 'ol':
          const ListTag = node.name;
          return (
            <ListTag
              key={index}
              style={{
                paddingLeft: '20px',
                margin: '0.5em 0',
                color: themeConfig[theme].colors.text,
              }}
            >
              {node.children?.map((child, i) =>
                convertNodeToElement(child, i, transform),
              )}
            </ListTag>
          );
        case 'li':
          return (
            <li
              key={index}
              style={{
                margin: '0.25em 0',
                color: themeConfig[theme].colors.text,
              }}
            >
              {node.children?.map((child, i) =>
                convertNodeToElement(child, i, transform),
              )}
            </li>
          );
      }
    }
    return undefined;
  };

  const cleanedValue = cleanText(value);

  return (
    <TextWrapper
      data-testid="text-component"
      $color={themeConfig[theme].colors.text}
      style={{ color: themeConfig[theme].colors.text, lineHeight: 'normal' }}
    >
      {ReactHtmlParser(cleanedValue, { transform })}
    </TextWrapper>
  );
};

const TextWrapper = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};

  p {
    margin: 0.5em 0;
    line-height: 1.6;
    white-space: pre-wrap;
  }

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }

  ul,
  ol {
    padding-left: 20px;
    margin: 0.5em 0;
  }

  li {
    margin: 0.25em 0;
  }

  blockquote {
    border-left: 4px solid #ddd;
    padding-left: 16px;
    margin: 16px 0;
    color: ${({ $color }) => $color}88;
  }

  code {
    background: #f5f5f5;
    padding: 2px 4px;
    border-radius: 4px;
  }
`;
